import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

function MainNavbar({ user, updateUser, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // 반응형 디자인을 위한 화면 크기 감지
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 메뉴 구성 (메모이제이션으로 성능 최적화)
  const menuConfig = useMemo(() => {
    const baseMenus = [
      { 
        label: '소개', 
        to: '/intro', 
        icon: '📖',
        description: 'Sportier 소개'
      },
      { 
        label: '운동 MBTI', 
        to: '/sports-mbti', 
        icon: '🧠',
        description: '나의 운동 성향 분석',
        isNew: true // 새로운 기능 표시
      },
      { 
        label: '대회', 
        to: '/tournaments', 
        icon: '🏆',
        description: '대회 참가하기'
      }
    ];

    if (user?.userType === 'association') {
      return [
        ...baseMenus,
        { 
          label: '대시보드', 
          to: '/association', 
          icon: '📊',
          description: '협회 관리',
          onlyLogin: true 
        },
        { 
          label: '대회 생성', 
          to: '/association/create-tournament', 
          icon: '➕',
          description: '새 대회 만들기',
          onlyLogin: true 
        },
        { 
          label: '랭킹', 
          to: '/ranking', 
          icon: '🏅',
          description: '순위 보기'
        },
        { 
          label: '커뮤니티', 
          to: '/community', 
          icon: '💬',
          description: '소통하기',
          onlyLogin: true 
        }
      ];
    } else {
      return [
        ...baseMenus,
        { 
          label: '매칭', 
          to: '/match', 
          icon: '⚔️',
          description: '경기 신청',
          onlyLogin: true 
        },
        { 
          label: '랭킹', 
          to: '/ranking', 
          icon: '🏅',
          description: '순위 보기'
        },
        { 
          label: '길드', 
          to: '/guild', 
          icon: '🏰',
          description: '길드 활동',
          onlyLogin: true 
        },
        { 
          label: '커뮤니티', 
          to: '/community', 
          icon: '💬',
          description: '소통하기',
          onlyLogin: true 
        }
      ];
    }
  }, [user?.userType]);

  // 인증 메뉴 (메모이제이션)
  const authMenus = useMemo(() => {
    if (user) {
      return [
        {
          label: '로그아웃',
          action: 'logout',
          icon: '🚪',
          style: 'danger'
        }
      ];
    } else {
      return [
        {
          label: '로그인',
          to: '/login',
          icon: '🔑',
          style: 'primary'
        }
      ];
    }
  }, [user]);

  // 로그아웃 핸들러 (최적화)
  const handleLogout = useCallback(() => {
    if (window.confirm('로그아웃 하시겠습니까?')) {
      updateUser(null);
      setShowMobileMenu(false);
      navigate('/');
    }
  }, [updateUser, navigate]);

  // 프로필 클릭 핸들러 (최적화)
  const handleProfileClick = useCallback(() => {
    if (user?.userType === 'association') {
      navigate("/association/profile");
    } else {
      navigate("/mypage");
    }
    setShowMobileMenu(false);
  }, [user?.userType, navigate]);

  // 로고 클릭 핸들러
  const handleLogoClick = useCallback(() => {
    navigate("/");
    setShowMobileMenu(false);
  }, [navigate]);

  // 모바일 메뉴 토글
  const toggleMobileMenu = useCallback(() => {
    setShowMobileMenu(prev => !prev);
  }, []);

  // 활성 경로 확인 (최적화)
  const isActiveRoute = useCallback((path) => {
    if (path === '/association' && location.pathname.startsWith('/association')) {
      return true;
    }
    return location.pathname === path;
  }, [location.pathname]);

  // 필터링된 메뉴 (권한 기반)
  const visibleMenus = useMemo(() => {
    return menuConfig.filter(menu => {
      if (menu.onlyLogin && !user) return false;
      if (menu.onlyGuest && user) return false;
      return true;
    });
  }, [menuConfig, user]);

  return (
    <>
      <nav style={styles.navbar}>
        <div style={{
          ...styles.container,
          padding: isMobile ? '16px 20px' : '12px 20px'
        }}>
          {/* 로고 섹션 */}
          <div style={styles.logoSection}>
            <span
              onClick={handleLogoClick}
              style={styles.logo}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              🏆 Sportier
            </span>
          </div>

          {/* 데스크톱 메뉴 */}
          {!isMobile && (
            <div style={styles.desktopMenu}>
              <div style={styles.menuItems}>
                {visibleMenus.map(menu => (
                  <Link
                    key={menu.to}
                    to={menu.to}
                    style={{
                      ...styles.menuItem,
                      ...(isActiveRoute(menu.to) ? styles.activeMenuItem : {}),
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActiveRoute(menu.to)) {
                        e.target.style.background = 'rgba(255,255,255,0.15)';
                        e.target.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActiveRoute(menu.to)) {
                        e.target.style.background = 'transparent';
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                    title={menu.description}
                  >
                    <span style={styles.menuIcon}>{menu.icon}</span>
                    {menu.label}
                    {/* NEW 뱃지 추가 */}
                    {menu.isNew && (
                      <span style={styles.newBadge}>NEW</span>
                    )}
                  </Link>
                ))}
              </div>

              {/* 우측 메뉴 */}
              <div style={{ ...styles.rightSection, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 18 }}>
                {/* 알림 벨 */}
                {user && children && (
                  <div style={{ ...styles.notificationWrapper, marginRight: 0 }}>{children}</div>
                )}
                {/* 사용자 정보 또는 로그인 버튼 */}
                {user ? (
                  <div style={{ ...styles.userInfo, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    {user.userType === 'association' && (
                      <span style={styles.userTypeBadge}>협회</span>
                    )}
                    <span
                      style={styles.username}
                      onClick={handleProfileClick}
                      title={user.userType === 'association' ? "협회 프로필" : "마이페이지"}
                    >
                      {user.nickname || user.associationName || user.id}
                    </span>
                    <button
                      onClick={handleLogout}
                      style={styles.logoutButton}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255,255,255,0.3)';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255,255,255,0.2)';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      🚪 로그아웃
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    style={styles.loginButton}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255,255,255,0.3)';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(255,255,255,0.2)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    🔑 로그인
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* 모바일 햄버거 메뉴 */}
          {isMobile && (
            <div style={styles.mobileSection}>
              {/* 알림 벨 (모바일) */}
              {user && children && (
                <div style={styles.mobileNotification}>
                  {children}
                </div>
              )}
              
              <button
                onClick={toggleMobileMenu}
                style={styles.hamburger}
                aria-label="메뉴 열기"
              >
                <div style={{
                  ...styles.hamburgerLine,
                  transform: showMobileMenu ? 'rotate(45deg) translate(5px, 5px)' : 'none'
                }}></div>
                <div style={{
                  ...styles.hamburgerLine,
                  opacity: showMobileMenu ? 0 : 1
                }}></div>
                <div style={{
                  ...styles.hamburgerLine,
                  transform: showMobileMenu ? 'rotate(-45deg) translate(7px, -6px)' : 'none'
                }}></div>
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* 모바일 드롭다운 메뉴 */}
      {isMobile && showMobileMenu && (
        <div style={styles.mobileDropdown}>
          <div style={styles.mobileMenuContent}>
            {/* 사용자 정보 섹션 */}
            {user && (
              <div style={styles.mobileUserSection}>
                <div style={styles.mobileUserInfo}>
                  {user.userType === 'association' && (
                    <span style={styles.mobileUserBadge}>협회</span>
                  )}
                  <span style={styles.mobileUsername}>
                    {user.nickname || user.associationName || user.id}
                  </span>
                </div>
                <button
                  onClick={handleProfileClick}
                  style={styles.mobileProfileButton}
                >
                  프로필 보기
                </button>
              </div>
            )}

            {/* 메뉴 아이템들 */}
            <div style={styles.mobileMenuItems}>
              {visibleMenus.map(menu => (
                <Link
                  key={menu.to}
                  to={menu.to}
                  style={{
                    ...styles.mobileMenuItem,
                    ...(isActiveRoute(menu.to) ? styles.activeMobileMenuItem : {})
                  }}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <span style={styles.mobileMenuIcon}>{menu.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={styles.mobileMenuLabel}>
                      {menu.label}
                      {menu.isNew && (
                        <span style={styles.mobileNewBadge}>NEW</span>
                      )}
                    </div>
                    <div style={styles.mobileMenuDescription}>{menu.description}</div>
                  </div>
                </Link>
              ))}
            </div>

            {/* 로그인/로그아웃 섹션 */}
            <div style={styles.mobileAuthSection}>
              {user ? (
                <button
                  onClick={handleLogout}
                  style={styles.mobileLogoutButton}
                >
                  🚪 로그아웃
                </button>
              ) : (
                <Link
                  to="/login"
                  style={styles.mobileLoginButton}
                  onClick={() => setShowMobileMenu(false)}
                >
                  🔑 로그인
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 모바일 메뉴 오버레이 */}
      {isMobile && showMobileMenu && (
        <div
          style={styles.overlay}
          onClick={() => setShowMobileMenu(false)}
        />
      )}
    </>
  );
}

// 스타일 정의 (성능 최적화를 위한 객체 분리)
const styles = {
  navbar: {
    width: '100%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  },

  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    position: 'relative'
  },

  logoSection: {
    flexShrink: 0
  },

  logo: {
    fontWeight: 900,
    fontSize: 28,
    color: '#fff',
    cursor: 'pointer',
    letterSpacing: '-1px',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
    transition: 'transform 0.2s ease',
    userSelect: 'none'
  },

  desktopMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: 40,
    flex: 1,
    justifyContent: 'space-between',
    marginLeft: 40
  },

  menuItems: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },

  menuItem: {
    color: '#fff',
    textDecoration: 'none',
    fontSize: 15,
    fontWeight: 600,
    padding: '10px 16px',
    borderRadius: 12,
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'transparent',
    border: '1px solid transparent',
    whiteSpace: 'nowrap'
  },

  activeMenuItem: {
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: '#FFD700',
    backdropFilter: 'blur(10px)'
  },

  menuIcon: {
    fontSize: 16
  },

  // NEW 뱃지 스타일
  newBadge: {
    background: '#ff4757',
    color: '#fff',
    fontSize: 8,
    fontWeight: 700,
    padding: '2px 4px',
    borderRadius: 4,
    marginLeft: 4,
    textTransform: 'uppercase',
    animation: 'pulse 2s infinite'
  },

  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 16
  },

  notificationWrapper: {
    display: 'flex',
    alignItems: 'center'
  },

  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: 'rgba(255,255,255,0.15)',
    borderRadius: 25,
    padding: '8px 16px',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)'
  },

  userTypeBadge: {
    background: '#FFD700',
    color: '#333',
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 10,
    textTransform: 'uppercase'
  },

  username: {
    color: '#FFD700',
    fontWeight: 700,
    fontSize: 15,
    cursor: 'pointer',
    textDecoration: 'underline',
    textShadow: '0 1px 2px rgba(0,0,0,0.3)'
  },

  logoutButton: {
    background: 'rgba(255,255,255,0.2)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backdropFilter: 'blur(10px)'
  },

  loginButton: {
    background: 'rgba(255,255,255,0.2)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: 12,
    padding: '10px 20px',
    fontSize: 15,
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },

  // 모바일 스타일
  mobileSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 12
  },

  mobileNotification: {
    display: 'flex',
    alignItems: 'center'
  },

  hamburger: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    transition: 'all 0.3s ease'
  },

  hamburgerLine: {
    width: 24,
    height: 3,
    background: '#fff',
    borderRadius: 2,
    transition: 'all 0.3s ease'
  },

  mobileDropdown: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    zIndex: 999,
    paddingTop: 80,
    maxHeight: '100vh',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch'
  },

  mobileMenuContent: {
    padding: '20px',
    maxWidth: '400px',
    margin: '0 auto'
  },

  mobileUserSection: {
    background: 'rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    textAlign: 'center'
  },

  mobileUserInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 12,
    flexWrap: 'wrap'
  },

  mobileUserBadge: {
    background: '#FFD700',
    color: '#333',
    fontSize: 12,
    fontWeight: 700,
    padding: '4px 8px',
    borderRadius: 8,
    textTransform: 'uppercase'
  },

  mobileUsername: {
    color: '#FFD700',
    fontWeight: 700,
    fontSize: 16
  },

  mobileProfileButton: {
    background: 'rgba(255,255,255,0.2)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer'
  },

  mobileMenuItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },

  mobileMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 20,
    background: 'rgba(255,255,255,0.1)',
    borderRadius: 16,
    color: '#fff',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    minHeight: 60,
    touchAction: 'manipulation'
  },

  activeMobileMenuItem: {
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: '#FFD700'
  },

  mobileMenuIcon: {
    fontSize: 20,
    width: 24,
    textAlign: 'center'
  },

  mobileMenuLabel: {
    fontSize: 16,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },

  // 모바일 NEW 뱃지
  mobileNewBadge: {
    background: '#ff4757',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 4,
    textTransform: 'uppercase'
  },

  mobileMenuDescription: {
    fontSize: 13,
    opacity: 0.8,
    marginTop: 2
  },

  mobileAuthSection: {
    marginTop: 20,
    paddingTop: 20,
    borderTop: '1px solid rgba(255,255,255,0.2)'
  },

  mobileLogoutButton: {
    width: '100%',
    background: 'rgba(255,255,255,0.2)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: 16,
    padding: 20,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minHeight: 56,
    touchAction: 'manipulation'
  },

  mobileLoginButton: {
    width: '100%',
    background: 'rgba(255,255,255,0.2)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: 16,
    padding: 20,
    fontSize: 16,
    fontWeight: 600,
    textDecoration: 'none',
    textAlign: 'center',
    display: 'block',
    transition: 'all 0.2s ease',
    minHeight: 56,
    touchAction: 'manipulation'
  },

  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 998
  }
};

// CSS 애니메이션을 위한 스타일시트 추가
const animationStyles = document.createElement('style');
animationStyles.textContent = `
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.7; }
    100% { opacity: 1; }
  }
`;
document.head.appendChild(animationStyles);

export default MainNavbar;